import {html, LitElement, ref, createRef} from "../libs/lit.dist.js";
import {modalsManager} from "../modals-manager.js";
import {getFistMatchingParent, getViewportSize} from "../util/browser.js";
import {AbstractComponentView} from "../views/abstract-component-view.js";
import {createElementWithProperties} from "./abstract-element.js";
import styles from './pk-dialog.css.js';

let nextDialogId = 1;

export const CLOSE_BUTTON_DEFAULT = 'default';
export const MODAL_POSITION_DEFAULT = '';

export const getParentDialog = (el) => {
    return getFistMatchingParent(el, PkDialog.componentName);
}

export const closeParentDialog = (el) => {
    const dialog = getParentDialog(el);
    dialog?.close();
    return dialog;
}

export const closeDialog = (dialogOrSelector) => {
    let dialog;
    if (typeof dialogOrSelector === "string") {
        dialog = document.querySelector(dialogOrSelector);
    } else {
        dialog = dialogOrSelector;
    }
    if (dialog instanceof PkDialog) {
        dialog.close();
    }
    return dialog;
}

export const openDialog = (dialogOrSelector) => {
    let dialog;
    if (typeof dialogOrSelector === "string") {
        dialog = document.querySelector(dialogOrSelector);
    } else {
        dialog = dialogOrSelector;
    }
    if (dialog instanceof PkDialog) {
        dialog.open();
    }
    return dialog;
}

export const createDialog = (options = {}) => {
    const {content, ...props} = options;
    const dialog = createElementWithProperties(PkDialog.componentName, props);
    dialog.innerHTML = content || '';
    document.body.appendChild(dialog);
    dialog.addEventListener('closed', () => {
        if (!dialog.minimized) {
            document.body.removeChild(dialog);
        }
    });
    return dialog;
};

export class PkDialog extends AbstractComponentView {

    static minDragAmount = 50;
    static dragScreenPadding = 25;

    #clickStartedOnDialog = false;
    innerDialogRef = createRef();
    dialogRef = createRef();
    bodyRef = createRef();

    static styles = styles;

    static get properties() {
        return {
            opened: {type: Boolean},
            title: {type: String},
            titleText: {type: String},
            header: {type: Boolean},
            headerStyle: {type: String},
            headerAlign: {type: String},
            size: {type: String},
            position: {type: String},
            closeButton: {type: String},
            minimizeButton: {type: Boolean},
            backdrop: {type: String},
            draggable: {type: Boolean},
            isVisible: {type: Boolean, state: true},
            isContentVisible: {type: Boolean, state: true},
        };
    }

    get dialog() {
        return this.dialogRef.value;
    }

    get innerDialog() {
        return this.innerDialogRef.value;
    }

    get body() {
        return this.bodyRef.value;
    }

    static create(props = {}) {
        const dialog = createElementWithProperties(PkDialog.componentName, props);
        document.body.appendChild(dialog);
        return dialog;
    }

    constructor() {
        super();
        this.cid = 'pk-dialog-' + (nextDialogId++);
        this.title = '';
        this.titleText = '';
        this.content = '';
        this.header = true;
        this.headerStyle = 'fancy';
        this.headerAlign = 'left'
        this.size = 'lg';
        this.position = '';
        this.closeButton = CLOSE_BUTTON_DEFAULT;
        this.minimizeButton = false;
        this.backdrop = true;
        this.draggable = true;
        this.isOpened = false;
        this.createdAt = new Date();
    }

    open() {
        this.opened = true;
    }

    close() {
        this.opened = false;
    }

    createRenderRoot() {
        return LitElement.prototype.createRenderRoot.call(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        modalsManager.closed(this);
    }

    updated(changedProps) {
        super.updated(changedProps);

        if (changedProps.has('opened')) {
            if (this.opened) {
                this.isContentVisible = true;
                setTimeout(() => {
                    this.classList.add('opened');
                    this.innerDialog?.focus();
                });
                modalsManager.opened(this);
                this.dispatchViewEvent('open');
                setTimeout(() => this.isVisible = true, 150);
            } else {
                const e = this.dispatchViewEvent('close');
                if (e.defaultPrevented) {
                    this.opened = true;
                } else {
                    if (!this.minimized) {
                        modalsManager.closed(this);
                    }
                    this.classList.remove('opened');
                    setTimeout(() => this.isVisible = false, 175);
                }
            }
        }

        if (changedProps.has('isVisible')) {
            if (this.isVisible) {
                this.dispatchViewEvent('opened');
            } else {
                this.isContentVisible = false;
                this.classList.remove('opened');
                this.dispatchViewEvent('closed');
            }
        }
    }

    handleDragStart(e) {
        if (this.draggable && this.innerDialog && this.position === MODAL_POSITION_DEFAULT && e.buttons === 1) {
            const rect = this.innerDialog.getBoundingClientRect();
            this.dragging = {
                startX: rect.x,
                startY: rect.y,
                offsetX: e.clientX - rect.x,
                offsetY: e.clientY - rect.y,
            };
        }
    }

    handleDragStop() {
        this.dragging = null;
    }

    handleDragging(e) {
        if (this.dragging && this.innerDialog && !this.__updatePositionRequested) {
            this.__updatePositionRequested = true;
            window.requestAnimationFrame(() => {
                this.__updatePositionRequested = false;
                if (!this.dragging) {
                    return;
                }
                const viewport = getViewportSize();
                const rect = this.innerDialog.getBoundingClientRect();

                const minX = 0 - rect.width + PkDialog.dragScreenPadding;
                const maxX = viewport.width - PkDialog.dragScreenPadding;
                const maxY = viewport.height - PkDialog.dragScreenPadding;
                const nextX = Math.min(maxX, Math.max(minX, e.clientX - this.dragging.offsetX));
                const nextY = Math.min(maxY, Math.max(0, e.clientY - this.dragging.offsetY));

                if (Math.abs(nextX - this.dragging.startX) < PkDialog.minDragAmount && Math.abs(nextY - this.dragging.startY) < PkDialog.minDragAmount) {
                    return;
                }

                this.innerDialog.style.margin = '0';
                this.innerDialog.style.left = nextX + 'px';
                this.innerDialog.style.top = nextY + 'px';
            });
        }
    }

    handleClickBackdrop(e) {
        if (!this.isVisible || e.x === 0 || e.y === 0) {
            return;
        }

        const els = this.renderRoot.elementsFromPoint(e.x, e.y);
        for (let el of els) {
            if (el.classList.contains('modal-dialog') || el.classList.contains('modal-content')) {
                return;
            }
        }

        const dialogs = modalsManager.getModalsFromPoint(e.x, e.y);
        const topMostModal = dialogs[dialogs.length - 1];
        if (topMostModal && !modalsManager.isModalOnTop(topMostModal)) {
            modalsManager.moveModalToTop(topMostModal);
            return;
        }

        if (this.isVisible && !this.#clickStartedOnDialog) {
            this.opened = false
        }
    }

    handleKeyDown(e) {
        if (this.isVisible && e.key === 'Escape') {
            this.opened = false;
        }
    }

    handleScroll(e) {
        this.dispatchViewEvent('scroll', e);
    }

    #handleMouseDownOnDialog(e) {
        e.stopImmediatePropagation();
        this.#clickStartedOnDialog = true;
    }

    #handleMouseDownOnBackdrop(e) {
        e.stopImmediatePropagation();
        this.#clickStartedOnDialog = false;
    }

    render() {
        if (!this.isContentVisible) {
            return;
        }

        return html`
            <div
                class="modal"
                ${ref(this.dialogRef)}
                @click="${this.handleClickBackdrop}"
                @keydown="${this.handleKeyDown}"
                @mousedown="${this.#handleMouseDownOnBackdrop}"
                @mouseup="${this.handleDragStop}"
                @mousemove="${this.handleDragging}"
                @scroll="${this.handleScroll}"
            >
                <div 
                    ${ref(this.innerDialogRef)}
                    @mousedown="${this.#handleMouseDownOnDialog}"
                    class="modal-dialog modal-${this.size} modal-dialog-${this.position || 'default'} ${this.header !== false ? 'modal-with-header' : ''}"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-${this.cid}-header"
                    tabindex="0"
                >
                    <div class="modal-content" ${ref(this.bodyRef)}>
                        <slot name="header">
                            ${this.header !== false ? html`
                                <section class="dialog-header" @mousedown="${this.handleDragStart}">
                                    <slot name="title" id="modal-${this.cid}-header">
                                        <h1 class="dialog-title">
                                            <slot name="title-text">
                                                ${this.titleText || this.title}
                                            </slot>
                                        </h1>
                                    </slot>
                                    <div class="dialog-controls">
                                        <slot name="buttons"></slot>
                                        <slot name="controls">
                                            ${this.minimizeButton ? html`
                                                <button type="button" class="minimize-button" @click="${() => modalsManager.minimized(this)}"></button>
                                            ` : null}
                                            ${this.closeButton === CLOSE_BUTTON_DEFAULT ? html`
                                                <button type="button" title="Zamknij" class="close-button" @click="${() => this.opened = false}"></button>
                                            ` : null}
                                        </slot>
                                    </div>
                                </section>
                            ` : null}
                        </slot>
                        <div class="modal-body">
                            <slot></slot>
                        </div>
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('pk-dialog', PkDialog);
